/**
 * LoadingStates - Componentes reutilizables para estados de carga
 *
 * Proporciona una experiencia de usuario consistente durante la carga de datos.
 */

import React from 'react';

// ============ SPINNER BÁSICO ============

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  className?: string;
}

export function Spinner({ size = 'md', color = 'primary', className = '' }: SpinnerProps) {
  const sizeClass = size === 'sm' ? 'spinner-border-sm' : '';

  return (
    <div
      className={`spinner-border text-${color} ${sizeClass} ${className}`}
      role="status"
      aria-label="Cargando..."
    >
      <span className="visually-hidden">Cargando...</span>
    </div>
  );
}

// ============ LOADING CON MENSAJE ============

interface LoadingMessageProps {
  message?: string;
  subMessage?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LoadingMessage({
  message = 'Cargando...',
  subMessage,
  size = 'md',
  className = ''
}: LoadingMessageProps) {
  return (
    <div className={`text-center py-4 ${className}`} role="alert" aria-busy="true">
      <Spinner size={size} />
      <p className="mt-3 mb-1 text-muted">{message}</p>
      {subMessage && <small className="text-muted">{subMessage}</small>}
    </div>
  );
}

// ============ LOADING PÁGINA COMPLETA ============

interface FullPageLoadingProps {
  message?: string;
  showLogo?: boolean;
}

export function FullPageLoading({
  message = 'Cargando...',
  showLogo = false
}: FullPageLoadingProps) {
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: '60vh' }}
      role="alert"
      aria-busy="true"
    >
      {showLogo && (
        <img
          src="/img/logo.png"
          alt="Logo AMPI"
          className="mb-4"
          style={{ maxWidth: '150px', opacity: 0.7 }}
        />
      )}
      <Spinner size="lg" />
      <p className="mt-3 text-muted fs-5">{message}</p>
    </div>
  );
}

// ============ SKELETON CARD ============

interface SkeletonCardProps {
  variant?: 'property' | 'user' | 'taller' | 'simple';
  className?: string;
}

export function SkeletonCard({ variant = 'simple', className = '' }: SkeletonCardProps) {
  const baseClass = "placeholder-glow";

  if (variant === 'property') {
    return (
      <div className={`card ${baseClass} ${className}`} aria-hidden="true">
        <div className="placeholder bg-secondary" style={{ height: '200px' }}></div>
        <div className="card-body">
          <div className="d-flex gap-2 mb-3">
            <span className="placeholder col-3 bg-secondary rounded"></span>
            <span className="placeholder col-3 bg-secondary rounded"></span>
          </div>
          <h5 className="card-title placeholder col-8 bg-secondary"></h5>
          <p className="card-text placeholder col-6 bg-secondary"></p>
          <div className="d-flex justify-content-between mt-3">
            <span className="placeholder col-4 bg-secondary"></span>
            <span className="placeholder col-2 bg-secondary rounded-circle" style={{ width: '40px', height: '40px' }}></span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'user') {
    return (
      <div className={`card ${baseClass} ${className}`} aria-hidden="true">
        <div className="card-body text-center">
          <div
            className="placeholder bg-secondary rounded-circle mx-auto mb-3"
            style={{ width: '100px', height: '100px' }}
          ></div>
          <h5 className="placeholder col-6 bg-secondary mx-auto"></h5>
          <p className="placeholder col-4 bg-secondary mx-auto"></p>
          <div className="d-flex justify-content-center gap-2 mt-3">
            <span className="placeholder col-3 bg-secondary"></span>
            <span className="placeholder col-3 bg-secondary"></span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'taller') {
    return (
      <div className={`card ${baseClass} ${className}`} aria-hidden="true">
        <div className="card-header">
          <span className="placeholder col-6 bg-secondary"></span>
        </div>
        <div className="card-body">
          <h5 className="placeholder col-8 bg-secondary"></h5>
          <p className="placeholder col-4 bg-secondary"></p>
          <p className="placeholder col-5 bg-secondary"></p>
          <p className="placeholder col-3 bg-secondary"></p>
          <div className="d-flex gap-2 mt-3">
            <span className="placeholder col-4 bg-primary rounded"></span>
          </div>
        </div>
      </div>
    );
  }

  // Simple variant
  return (
    <div className={`card ${baseClass} ${className}`} aria-hidden="true">
      <div className="card-body">
        <h5 className="placeholder col-6 bg-secondary"></h5>
        <p className="placeholder col-8 bg-secondary"></p>
        <p className="placeholder col-4 bg-secondary"></p>
      </div>
    </div>
  );
}

// ============ SKELETON GRID ============

interface SkeletonGridProps {
  count?: number;
  columns?: 2 | 3 | 4;
  variant?: 'property' | 'user' | 'taller' | 'simple';
  className?: string;
}

export function SkeletonGrid({
  count = 6,
  columns = 3,
  variant = 'simple',
  className = ''
}: SkeletonGridProps) {
  const colClass = columns === 2 ? 'col-md-6' :
                   columns === 3 ? 'col-lg-4 col-md-6' :
                   'col-lg-3 col-md-4 col-sm-6';

  return (
    <div className={`row g-4 ${className}`} aria-label="Cargando contenido..." role="status">
      {Array.from({ length: count }).map((_, index) => (
        <div className={colClass} key={index}>
          <SkeletonCard variant={variant} />
        </div>
      ))}
    </div>
  );
}

// ============ SKELETON TABLE ============

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeader = true,
  className = ''
}: SkeletonTableProps) {
  return (
    <div className={`table-responsive ${className}`} aria-label="Cargando tabla..." role="status">
      <table className="table placeholder-glow" aria-hidden="true">
        {showHeader && (
          <thead>
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i}><span className="placeholder col-8 bg-secondary"></span></th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex}>
                  <span className={`placeholder col-${colIndex === 0 ? 10 : 6 + (colIndex % 3)} bg-secondary`}></span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ LOADING INLINE ============

interface InlineLoadingProps {
  text?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function InlineLoading({
  text = 'Cargando',
  size = 'sm',
  className = ''
}: InlineLoadingProps) {
  return (
    <span className={`d-inline-flex align-items-center ${className}`} role="status">
      <Spinner size={size} className="me-2" />
      <span>{text}...</span>
    </span>
  );
}

// ============ BUTTON LOADING ============

interface ButtonLoadingProps {
  loading: boolean;
  loadingText?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline-primary' | 'outline-secondary';
  onClick?: () => void;
}

export function ButtonLoading({
  loading,
  loadingText = 'Procesando',
  children,
  className = '',
  disabled = false,
  type = 'button',
  variant = 'primary',
  onClick
}: ButtonLoadingProps) {
  return (
    <button
      type={type}
      className={`btn btn-${variant} ${className}`}
      disabled={loading || disabled}
      onClick={onClick}
    >
      {loading ? (
        <>
          <Spinner size="sm" color={variant.includes('outline') ? variant.replace('outline-', '') as any : 'light'} className="me-2" />
          {loadingText}...
        </>
      ) : (
        children
      )}
    </button>
  );
}

// ============ ERROR STATE ============

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryText?: string;
  className?: string;
}

export function ErrorState({
  title = 'Error al cargar',
  message = 'Ocurrió un problema al obtener los datos.',
  onRetry,
  retryText = 'Reintentar',
  className = ''
}: ErrorStateProps) {
  return (
    <div className={`text-center py-5 ${className}`} role="alert">
      <div className="mb-3">
        <i className="fa-solid fa-circle-exclamation text-danger fa-3x" aria-hidden="true"></i>
      </div>
      <h5 className="text-danger">{title}</h5>
      <p className="text-muted">{message}</p>
      {onRetry && (
        <button className="btn btn-outline-primary mt-2" onClick={onRetry}>
          <i className="fa-solid fa-rotate-right me-2" aria-hidden="true"></i>
          {retryText}
        </button>
      )}
    </div>
  );
}

// ============ EMPTY STATE ============

interface EmptyStateProps {
  icon?: string;
  title?: string;
  message?: string;
  actionText?: string;
  onAction?: () => void;
  className?: string;
}

export function EmptyState({
  icon = 'fa-solid fa-inbox',
  title = 'Sin datos',
  message = 'No hay elementos para mostrar.',
  actionText,
  onAction,
  className = ''
}: EmptyStateProps) {
  return (
    <div className={`text-center py-5 ${className}`}>
      <div className="mb-3">
        <i className={`${icon} text-muted fa-3x`} aria-hidden="true"></i>
      </div>
      <h5 className="text-muted">{title}</h5>
      <p className="text-muted">{message}</p>
      {actionText && onAction && (
        <button className="btn btn-primary mt-2" onClick={onAction}>
          {actionText}
        </button>
      )}
    </div>
  );
}

// ============ ESTADÍSTICAS SKELETON ============

interface SkeletonStatsProps {
  count?: number;
  className?: string;
}

export function SkeletonStats({ count = 4, className = '' }: SkeletonStatsProps) {
  return (
    <div className={`row g-3 ${className}`} aria-label="Cargando estadísticas..." role="status">
      {Array.from({ length: count }).map((_, index) => (
        <div className="col-md-3 col-sm-6" key={index}>
          <div className="card placeholder-glow" aria-hidden="true">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="placeholder col-4 bg-secondary mb-2" style={{ height: '24px' }}></h5>
                  <small className="placeholder col-6 bg-secondary"></small>
                </div>
                <span
                  className="placeholder bg-secondary rounded"
                  style={{ width: '48px', height: '48px' }}
                ></span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ SKELETON: PERFIL (agent-page / asociado-page) ============

interface ProfilePageSkeletonProps {
  color?: 'primary' | 'success';
}

export function ProfilePageSkeleton({ color = 'primary' }: ProfilePageSkeletonProps) {
  return (
    <>
      {/* Hero skeleton */}
      <section className={`bg-${color} py-4`}>
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-4 placeholder-glow">
            <div
              className="placeholder bg-light rounded-circle"
              style={{ width: '150px', height: '150px', flexShrink: 0, opacity: 0.3 }}
            />
            <div style={{ flex: 1 }}>
              <div className="placeholder bg-light rounded mb-2" style={{ width: '260px', height: '28px', opacity: 0.3 }} />
              <div className="placeholder bg-light rounded mb-3" style={{ width: '180px', height: '16px', opacity: 0.3 }} />
              <div className="d-flex gap-2 justify-content-center justify-content-md-start">
                <span className="placeholder bg-light rounded" style={{ width: '120px', height: '24px', opacity: 0.3 }} />
                <span className="placeholder bg-light rounded" style={{ width: '100px', height: '24px', opacity: 0.3 }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content skeleton */}
      <section className="gray-simple">
        <div className="container">
          <div className="row">
            {/* Main col */}
            <div className="col-lg-8 col-md-12">
              {/* Bio card */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '160px', height: '22px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '100%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '90%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded" style={{ width: '70%', height: '14px' }} />
                </div>
              </div>

              {/* Rating card */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '200px', height: '22px' }} />
                  <div className="d-flex align-items-center gap-2 mb-3">
                    {[1,2,3,4,5].map(i => (
                      <span key={i} className="placeholder bg-warning rounded" style={{ width: '24px', height: '24px' }} />
                    ))}
                    <span className="placeholder bg-secondary rounded ms-2" style={{ width: '40px', height: '20px' }} />
                  </div>
                  <div className="placeholder bg-secondary rounded" style={{ width: '180px', height: '14px' }} />
                </div>
              </div>

              {/* Reviews card */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '120px', height: '22px' }} />
                  <div className="card p-4 text-center border">
                    <div className="placeholder bg-secondary rounded mx-auto mb-2" style={{ width: '80%', height: '14px' }} />
                    <div className="placeholder bg-secondary rounded mx-auto mb-3" style={{ width: '60%', height: '14px' }} />
                    <div className="d-flex justify-content-center gap-1">
                      {[1,2,3,4,5].map(i => (
                        <span key={i} className="placeholder bg-warning rounded" style={{ width: '16px', height: '16px' }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              {/* Contact card */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '120px', height: '22px' }} />
                  <div className="d-flex align-items-center mb-3">
                    <span className="placeholder bg-secondary rounded-circle me-3" style={{ width: '20px', height: '20px' }} />
                    <div>
                      <div className="placeholder bg-secondary rounded mb-1" style={{ width: '60px', height: '12px' }} />
                      <div className="placeholder bg-secondary rounded" style={{ width: '120px', height: '16px' }} />
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-4">
                    <span className="placeholder bg-secondary rounded-circle me-3" style={{ width: '20px', height: '20px' }} />
                    <div>
                      <div className="placeholder bg-secondary rounded mb-1" style={{ width: '40px', height: '12px' }} />
                      <div className="placeholder bg-secondary rounded" style={{ width: '160px', height: '16px' }} />
                    </div>
                  </div>
                  <hr />
                  <div className="d-grid gap-2">
                    <span className="placeholder bg-success rounded" style={{ height: '38px' }} />
                    <span className={`placeholder bg-${color} rounded`} style={{ height: '38px' }} />
                    <span className="placeholder bg-secondary rounded" style={{ height: '38px', opacity: 0.4 }} />
                  </div>
                </div>
              </div>

              {/* Inmobiliaria card */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '130px', height: '22px' }} />
                  <div className="d-flex align-items-center">
                    <span className="placeholder bg-secondary rounded me-3" style={{ width: '50px', height: '50px' }} />
                    <div>
                      <div className="placeholder bg-secondary rounded mb-1" style={{ width: '140px', height: '16px' }} />
                      <div className="placeholder bg-secondary rounded" style={{ width: '100px', height: '12px' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============ SKELETON: AGENCY PAGE ============

export function AgencyPageSkeleton() {
  return (
    <>
      {/* Hero skeleton */}
      <section className="bg-primary py-4">
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-4 placeholder-glow">
            <div
              className="placeholder bg-light rounded"
              style={{ width: '120px', height: '120px', flexShrink: 0, opacity: 0.3 }}
            />
            <div style={{ flex: 1 }}>
              <div className="placeholder bg-light rounded mb-2" style={{ width: '280px', height: '28px', opacity: 0.3 }} />
              <div className="placeholder bg-light rounded mb-3" style={{ width: '220px', height: '16px', opacity: 0.3 }} />
              <div className="d-flex gap-2 justify-content-center justify-content-md-start">
                <span className="placeholder bg-light rounded" style={{ width: '100px', height: '24px', opacity: 0.3 }} />
                <span className="placeholder bg-light rounded" style={{ width: '80px', height: '24px', opacity: 0.3 }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content skeleton */}
      <section className="gray-simple">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              {/* Description */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '200px', height: '22px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '100%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '85%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded" style={{ width: '60%', height: '14px' }} />
                </div>
              </div>

              {/* Team tabs */}
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '100px', height: '22px' }} />
                  <div className="d-flex gap-2 mb-4">
                    <span className="placeholder bg-primary rounded" style={{ width: '130px', height: '36px' }} />
                    <span className="placeholder bg-secondary rounded" style={{ width: '120px', height: '36px', opacity: 0.4 }} />
                  </div>
                  <div className="row g-3">
                    {[1,2,3,4].map(i => (
                      <div className="col-lg-6 col-md-6" key={i}>
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center">
                              <span className="placeholder bg-secondary rounded-circle me-3" style={{ width: '70px', height: '70px', flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div className="placeholder bg-secondary rounded mb-1" style={{ width: '120px', height: '16px' }} />
                                <div className="placeholder bg-secondary rounded" style={{ width: '90px', height: '12px' }} />
                              </div>
                            </div>
                            <div className="placeholder bg-secondary rounded mt-3" style={{ width: '100%', height: '32px', opacity: 0.4 }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '120px', height: '22px' }} />
                  {[1,2,3].map(i => (
                    <div className="d-flex align-items-center mb-3" key={i}>
                      <span className="placeholder bg-secondary rounded-circle me-3" style={{ width: '20px', height: '20px' }} />
                      <div>
                        <div className="placeholder bg-secondary rounded mb-1" style={{ width: '60px', height: '12px' }} />
                        <div className="placeholder bg-secondary rounded" style={{ width: `${100 + i * 20}px`, height: '16px' }} />
                      </div>
                    </div>
                  ))}
                  <hr />
                  <div className="d-grid gap-2">
                    <span className="placeholder bg-success rounded" style={{ height: '38px' }} />
                    <span className="placeholder bg-primary rounded" style={{ height: '38px' }} />
                    <span className="placeholder bg-secondary rounded" style={{ height: '38px', opacity: 0.4 }} />
                  </div>
                </div>
              </div>

              <div className="card shadow-sm border-0 mb-4 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '130px', height: '22px' }} />
                  {[1,2,3].map(i => (
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom" key={i}>
                      <div className="placeholder bg-secondary rounded" style={{ width: '100px', height: '14px' }} />
                      <span className="placeholder bg-primary rounded" style={{ width: '30px', height: '22px' }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============ SKELETON: PROPERTY DETAIL PAGE (single-property-1) ============

export function PropertyPageSkeleton() {
  return (
    <>
      {/* Gallery skeleton */}
      <div className="featured_slick_gallery gray py-4">
        <div className="container">
          <div className="placeholder-glow" aria-hidden="true">
            <div className="placeholder bg-secondary rounded" style={{ width: '100%', height: '400px' }} />
          </div>
        </div>
      </div>

      {/* Content skeleton */}
      <section className="gray-simple">
        <div className="container">
          <div className="row">
            {/* Main col */}
            <div className="col-lg-8 col-md-12">
              <div className="property_block_wrap style-2 p-4 placeholder-glow" aria-hidden="true">
                {/* Badges */}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <span className="placeholder bg-primary rounded" style={{ width: '90px', height: '22px' }} />
                  <span className="placeholder bg-success rounded" style={{ width: '80px', height: '22px' }} />
                  <span className="placeholder bg-secondary rounded" style={{ width: '100px', height: '22px' }} />
                </div>

                {/* Title */}
                <div className="placeholder bg-secondary rounded mb-2" style={{ width: '70%', height: '28px' }} />

                {/* Location */}
                <div className="placeholder bg-secondary rounded mb-3" style={{ width: '45%', height: '16px' }} />

                {/* Price row */}
                <div className="d-flex align-items-center flex-wrap gap-3 mb-4 pb-3 border-bottom">
                  <div className="placeholder bg-secondary rounded" style={{ width: '200px', height: '32px' }} />
                  <div className="placeholder bg-secondary rounded" style={{ width: '80px', height: '28px' }} />
                </div>

                {/* Features strip - 4 columns */}
                <div className="row text-center g-2 mb-4">
                  {['Recámaras', 'Baños', 'm²', 'Parking'].map((label, i) => (
                    <div className="col" key={i}>
                      <div className="bg-light rounded p-3">
                        <div className="placeholder bg-secondary rounded-circle mx-auto mb-1" style={{ width: '24px', height: '24px' }} />
                        <div className="placeholder bg-secondary rounded mx-auto mb-1" style={{ width: '30px', height: '18px' }} />
                        <div className="placeholder bg-secondary rounded mx-auto" style={{ width: '50px', height: '12px', opacity: 0.5 }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <div className="mb-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '120px', height: '22px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '100%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '95%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '88%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded mb-2" style={{ width: '92%', height: '14px' }} />
                  <div className="placeholder bg-secondary rounded" style={{ width: '60%', height: '14px' }} />
                </div>

                {/* Accordion placeholders */}
                {['Características', 'Amenidades', 'Ubicación'].map((title, i) => (
                  <div className="card mb-2" key={i}>
                    <div className="card-header d-flex align-items-center">
                      <span className="placeholder bg-secondary rounded" style={{ width: `${100 + i * 20}px`, height: '18px' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              <div className="card shadow-sm border-0 placeholder-glow" aria-hidden="true">
                <div className="card-body p-4">
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '160px', height: '22px' }} />
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '100%', height: '40px' }} />
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '100%', height: '40px' }} />
                  <div className="placeholder bg-secondary rounded mb-3" style={{ width: '100%', height: '80px' }} />
                  <div className="placeholder bg-primary rounded" style={{ width: '100%', height: '42px' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ============ SKELETON: LISTING GRID (agencies, agents, asociados) ============

interface ListingGridSkeletonProps {
  color?: 'primary' | 'success';
  cardShape?: 'circle' | 'square';
  count?: number;
}

export function ListingGridSkeleton({ color = 'primary', cardShape = 'circle', count = 8 }: ListingGridSkeletonProps) {
  return (
    <div className="row justify-content-center g-4">
      {Array.from({ length: count }).map((_, i) => (
        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={i}>
          <div className="card rounded-3 shadow h-100 placeholder-glow" aria-hidden="true">
            <div className="text-center mt-4 mb-3">
              <div
                className={`placeholder bg-secondary mx-auto ${cardShape === 'circle' ? 'rounded-circle' : 'rounded'}`}
                style={{ width: '130px', height: '130px' }}
              />
            </div>
            <div className="text-center px-3">
              <div className="placeholder bg-secondary rounded mx-auto mb-2" style={{ width: '140px', height: '18px' }} />
              <div className="d-flex align-items-center justify-content-center mb-2">
                <span className="placeholder bg-secondary rounded" style={{ width: '120px', height: '14px' }} />
              </div>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <span className="placeholder bg-secondary rounded" style={{ width: '150px', height: '14px', opacity: 0.5 }} />
              </div>
            </div>
            <div className="px-4 py-4 mt-auto">
              <div className={`placeholder bg-${color} rounded`} style={{ width: '100%', height: '38px', opacity: 0.4 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ EXPORTAR TODO ============

export default {
  Spinner,
  LoadingMessage,
  FullPageLoading,
  SkeletonCard,
  SkeletonGrid,
  SkeletonTable,
  InlineLoading,
  ButtonLoading,
  ErrorState,
  EmptyState,
  SkeletonStats,
  ProfilePageSkeleton,
  AgencyPageSkeleton,
  PropertyPageSkeleton,
  ListingGridSkeleton
};
